import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const videoConfigSchema = z.object({
  artifactId: z.string().default(''),
  runtimeId: z.string().default(''),
  src: z.string().default(''),
  poster: z.string().default(''),
  title: z.string().default(''),
  controls: z.boolean().default(true),
  autoplay: z.boolean().default(false),
  loop: z.boolean().default(false),
  muted: z.boolean().default(false),
  objectFit: z.enum(['contain', 'cover']).default('contain'),
  showChapters: z.boolean().default(true),
}).strict();

export const videoDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.video@1',
    category: 'media',
    title: '教学视频',
    description: '播放已登记或受信任地址的视频资源。',
    capabilities: [
      'video-playback',
      'artifact-reference',
      'captions',
      'chapters',
      'timeline-cues',
    ],
    suitableFor: ['过程演示', '现象观察', '讲解片段', '教学视频'],
    inputs: [],
    outputs: [],
    events: [
      {
        type: 'media.time.updated',
        description: '媒体播放时间发生变化',
      },
      {
        type: 'timeline.seek',
        description: '用户选择视频章节',
      },
      {
        type: 'timeline.cue.entered',
        description: '播放位置进入 Artifact cue',
        configurable: true,
      },
    ],
    constraints: [
      '仅允许 http、https 或 blob 资源',
      '不使用 iframe、srcdoc 或脚本',
      '默认关闭自动播放',
    ],
    configSchemaId: 'probfun.video.config@1',
    configSchema: {
      type: 'object',
      properties: {
        artifactId: { type: 'string' },
        runtimeId: { type: 'string' },
        src: { type: 'string' },
        poster: { type: 'string' },
        title: { type: 'string' },
        controls: { type: 'boolean' },
        autoplay: { type: 'boolean', default: false },
        loop: { type: 'boolean', default: false },
        muted: { type: 'boolean', default: false },
        objectFit: {
          type: 'string',
          enum: ['contain', 'cover'],
          default: 'contain',
        },
        showChapters: { type: 'boolean', default: true },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      artifactId: '',
      runtimeId: '',
      src: '',
      poster: '',
      title: '',
      controls: true,
      autoplay: false,
      loop: false,
      muted: false,
      objectFit: 'contain',
      showChapters: true,
    },
    examples: [{
      artifactId: 'lesson-video',
      runtimeId: '',
      src: '',
      poster: '',
      title: '过程讲解',
      controls: true,
      autoplay: false,
      loop: false,
      muted: false,
      objectFit: 'contain',
      showChapters: true,
    }],
    notes: ['视频产物由后端或人工流程生成，前端只负责安全播放。'],
    layout: {
      ...componentHardMinimum('probfun.video@1'),
      preferredAspectRatio: 1.7778,
      maxWidth: 1200,
      maxHeight: 900,
    },
    runtimeRequirements: [],
    metadata: {
      layoutHints: {
        roles: ['media'],
        preferredSize: 'medium',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: false,
        priority: 40,
      },
    },
  },
  configSchema: videoConfigSchema,
  loader: () => import('./VideoBlock.vue').then(module => module.default),
};
